import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../../../components/ui';
import { useAuth } from '../../../app/auth';
import { mockUsers } from '../../../mock/users.mock';
import { Search, CheckCircle, XCircle, AlertTriangle, RefreshCw, Trophy, Printer } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { formatTimeCR } from '../../../utils/date';

interface JugadaGanadora {
  num1: string;
  num2: string | null;
  monto_apostado: string;
  multiplicador: string;
  premio: string;
  concepto: string;
}

interface TicketConsulta {
  ticket_id: string;
  ticket_number: string;
  sorteo_id: string;
  sorteo_nombre: string;
  vendedor_id: string;
  metodo_pago: string;
  total: string;
  estado: string;
  created_at: string;
  premio_calculado: string;
  jugadas_ganadoras: JugadaGanadora[];
  caja_abierta: boolean;
  ya_premiado: boolean;
  anulado: boolean;
  es_credito: boolean;
  saldo_credito: string | null;
  cliente_nombre: string;
}

interface PremioHistorial {
  id: string;
  ticket_id: string;
  ticket_number: string | null;
  monto: string;
  usuario_id: string;
  created_at: string;
}

interface NumeroGanador {
  id: number;
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  fecha: string;
  hora_cierre: string;
  num1: string;
  num2: string | null;
  num3: string | null;
  tipo_premio: string | null;
  tipo_monazos: string | null;
  paga_reventado: boolean | null;
  registrado_por: string;
  created_at: string;
  verificado: boolean;
}

// Formatear número ganador según el tipo de sorteo
const formatearNumeroGanador = (g: NumeroGanador): string => {
  const tipo = g.tipo_id;
  
  switch (tipo) {
    case 'NORMAL':
      return g.num1.padStart(2, '0');
    
    case 'REVENTADO':
      if (g.tipo_premio && g.tipo_premio !== 'PRINCIPAL') {
        return `${g.num1.padStart(2, '0')} (${g.tipo_premio})`;
      }
      return g.num1.padStart(2, '0');
    
    case 'MEGA':
      let megaStr = g.num1.padStart(2, '0');
      if (g.num2) megaStr += ` - ${g.num2.padStart(2, '0')}`;
      if (g.tipo_premio) megaStr += ` (${g.tipo_premio})`;
      return megaStr;
    
    case 'MONAZOS':
      let monazoStr = g.num1.padStart(3, '0');
      if (g.tipo_monazos) monazoStr += ` (${g.tipo_monazos})`;
      return monazoStr;
    
    case 'FECHAS':
      if (g.num2) {
        return `${g.num1.padStart(2, '0')}-${g.num2.padStart(2, '0')}`;
      }
      return g.num1;
    
    case 'PARLEY':
      if (g.num2) {
        return `${g.num1.padStart(2, '0')} + ${g.num2.padStart(2, '0')}`;
      }
      return g.num1.padStart(2, '0');
    
    default:
      return g.num1;
  }
};

// ── Reporte de ganadores para impresión ─────────────────

interface TicketGanador {
  ticket_number: string;
  ticket_id: string;
  num_jugado: string;
  monto_jugado: string;
  concepto: string;
  estado: string;
  cliente_nombre: string;
}

interface GanadorInfo {
  ganador_id: number;
  numero: string;
  num1: string;
  num2: string | null;
  verificado: boolean;
  tickets: TicketGanador[];
}

interface SorteoReporte {
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  hora_cierre: string;
  ganadores: GanadorInfo[];
}

function generarHTMLReporteGanadores(reporte: SorteoReporte[]): string {
  const hoy = new Date().toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  let sorteosSections = '';
  for (const sorteo of reporte) {
    let ganadoresRows = '';
    for (const g of sorteo.ganadores) {
      const numDisplay = g.numero;
      const verificadoMark = g.verificado ? ' ✓' : ' ?';

      if (g.tickets.length === 0) {
        ganadoresRows += `
          <tr>
            <td style="padding:2px 4px;font-weight:bold;font-size:14px;font-family:monospace;">${numDisplay}${verificadoMark}</td>
            <td style="padding:2px 4px;color:#999;font-size:11px;" colspan="2">Sin tickets</td>
          </tr>`;
      } else {
        for (let i = 0; i < g.tickets.length; i++) {
          const t = g.tickets[i];
          const estado = t.estado === 'PREMIADO' ? ' (PAGADO)' : '';
          const cliente = t.cliente_nombre || 'Cliente';
          ganadoresRows += `
            <tr>
              ${i === 0 ? `<td style="padding:2px 4px;font-weight:bold;font-size:14px;font-family:monospace;vertical-align:top;" rowspan="${g.tickets.length}">${numDisplay}${verificadoMark}</td>` : ''}
              <td style="padding:2px 4px;font-size:12px;"><div style="font-family:monospace;">${t.ticket_number}</div><div style="font-size:10px;color:#555;">${cliente}</div></td>
              <td style="padding:2px 4px;text-align:right;font-size:12px;">₡${parseFloat(t.monto_jugado).toLocaleString()}${estado}</td>
            </tr>`;
        }
      }
    }

    sorteosSections += `
      <div style="margin-bottom:10px;">
        <div style="background:#000;color:#fff;padding:4px 6px;font-weight:bold;font-size:13px;">
          ${sorteo.sorteo_nombre} (${sorteo.hora_cierre}) - ${sorteo.tipo_id}
        </div>
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="border-bottom:1px solid #000;">
              <th style="text-align:left;padding:2px 4px;font-size:11px;">Número</th>
              <th style="text-align:left;padding:2px 4px;font-size:11px;">Ticket / Cliente</th>
              <th style="text-align:right;padding:2px 4px;font-size:11px;">Premio</th>
            </tr>
          </thead>
          <tbody>
            ${ganadoresRows}
          </tbody>
        </table>
      </div>`;
  }

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Reporte Ganadores</title>
  <style>
    @page { margin: 2mm; }
    body { font-family: Arial, sans-serif; margin: 0; padding: 4px; width: 72mm; }
    * { box-sizing: border-box; }
  </style>
</head>
<body>
  <div style="text-align:center;border-bottom:2px solid #000;padding-bottom:4px;margin-bottom:6px;">
    <div style="font-size:16px;font-weight:bold;">🏆 GANADORES DEL DÍA</div>
    <div style="font-size:12px;">${hoy}</div>
  </div>
  ${sorteosSections}
  <div style="border-top:1px dashed #000;margin-top:6px;padding-top:4px;text-align:center;font-size:10px;color:#666;">
    Reporte generado ${formatTimeCR(new Date().toISOString())}
  </div>
</body>
</html>`;
}

export const ValidacionPremiosPage: React.FC = () => {
  const { user } = useAuth();
  const localId = user?.localId || 'L001';
  const inputRef = useRef<HTMLInputElement>(null);

  const [barcode, setBarcode] = useState('');
  const [consulta, setConsulta] = useState<TicketConsulta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [exito, setExito] = useState<string | null>(null);
  const [historial, setHistorial] = useState<PremioHistorial[]>([]);
  const [ganadores, setGanadores] = useState<NumeroGanador[]>([]);
  const [loadingGanadores, setLoadingGanadores] = useState(false);
  const [imprimiendoReporte, setImprimiendoReporte] = useState(false);

  const nombreVendedor = (id: string) => {
    const u = mockUsers.find(u => u.id === id);
    return u ? u.nombre : id;
  };

  const cargarHistorial = () => {
    fetchAPI<PremioHistorial[]>(`/premios/historial?local_id=${localId}`)
      .then(data => setHistorial(data))
      .catch(() => {});
  };

  const cargarGanadores = () => {
    setLoadingGanadores(true);
    fetchAPI<NumeroGanador[]>('/ganadores/hoy')
      .then(data => setGanadores(data))
      .catch(() => setGanadores([]))
      .finally(() => setLoadingGanadores(false));
  };

  const imprimirReporteSorteo = async (sorteoNombre: string) => {
    if (imprimiendoReporte) return;
    setImprimiendoReporte(true);
    try {
      const reporte = await fetchAPI<SorteoReporte[]>('/ganadores/hoy/reporte');
      if (!reporte || reporte.length === 0) {
        setError('No hay ganadores registrados para imprimir');
        return;
      }

      // Filtrar solo el sorteo seleccionado
      const reporteFiltrado = reporte.filter(r => r.sorteo_nombre === sorteoNombre);
      if (reporteFiltrado.length === 0) {
        setError(`No hay datos de reporte para ${sorteoNombre}`);
        return;
      }

      const html = generarHTMLReporteGanadores(reporteFiltrado);
      const printWindow = window.open('', '_blank', 'width=420,height=700');
      if (!printWindow) {
        setError('No se pudo abrir la ventana de impresión');
        return;
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 300);
      };
    } catch (e: any) {
      setError(e.message || 'Error al generar reporte de ganadores');
    } finally {
      setImprimiendoReporte(false);
    }
  };

  // Agrupar ganadores por sorteo
  const ganadoresPorSorteo = ganadores.reduce<Record<string, { sorteo_nombre: string; hora_cierre: string; items: NumeroGanador[] }>>((acc, g) => {
    const key = g.sorteo_nombre;
    if (!acc[key]) {
      acc[key] = { sorteo_nombre: g.sorteo_nombre, hora_cierre: g.hora_cierre, items: [] };
    }
    acc[key].items.push(g);
    return acc;
  }, {});

  useEffect(() => {
    cargarHistorial();
    cargarGanadores();
    inputRef.current?.focus();
  }, []);

  const consultarTicket = async () => {
    if (!barcode.trim()) return;
    setLoading(true);
    setError(null);
    setConsulta(null);
    setExito(null);

    try {
      const data = await fetchAPI<TicketConsulta>(`/premios/consultar/${barcode.trim()}?local_id=${localId}`);
      setConsulta(data);
    } catch (e: any) {
      setError(e.message || 'Error consultando ticket');
    } finally {
      setLoading(false);
    }
  };

  const confirmarPago = async () => {
    if (!consulta) return;
    setConfirmando(true);
    setError(null);

    try {
      const premio = await fetchAPI<any>('/premios/validar', {
        method: 'POST',
        body: JSON.stringify({
          barcode: consulta.ticket_number,
          usuario_id: user?.id || '0',
          local_id: localId,
        }),
      });
      if (premio.es_credito && premio.abono_credito) {
        setExito(`Premio aplicado como abono: ${'\u20A1'}${parseFloat(premio.abono_credito).toLocaleString()} — Saldo restante: ${'\u20A1'}${parseFloat(premio.saldo_restante || '0').toLocaleString()}`);
      } else {
        setExito(`Premio pagado: ${'\u20A1'}${parseFloat(premio.monto).toLocaleString()}`);
      }
      setConsulta(null);
      setBarcode('');
      cargarHistorial();
      inputRef.current?.focus();
    } catch (e: any) {
      setError(e.message || 'Error al pagar premio');
    } finally {
      setConfirmando(false);
    }
  };

  const limpiar = () => {
    setBarcode('');
    setConsulta(null);
    setError(null);
    setExito(null);
    inputRef.current?.focus();
  };

  const puedeConfirmar = consulta && !consulta.ya_premiado && !consulta.anulado && consulta.estado === 'ABIERTO' && consulta.jugadas_ganadoras && consulta.jugadas_ganadoras.length > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Validacion de Premios</h1>
          <p className="text-gray-500">Escanea el codigo de barras del ticket</p>
        </div>
        <button
          onClick={() => { cargarHistorial(); cargarGanadores(); }}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
          title="Refrescar"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Números Ganadores del Día - agrupados por sorteo */}
      {loadingGanadores ? (
        <Card>
          <div className="text-center py-4 text-gray-400">Cargando ganadores...</div>
        </Card>
      ) : ganadores.length === 0 ? (
        <Card>
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="text-yellow-500" size={20} />
            <h3 className="font-semibold text-gray-900">Números Ganadores de Hoy</h3>
          </div>
          <p className="text-center text-gray-400 py-4 text-sm">
            No hay números ganadores registrados para hoy
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {Object.values(ganadoresPorSorteo).map(grupo => (
            <button
              key={grupo.sorteo_nombre}
              onClick={() => imprimirReporteSorteo(grupo.sorteo_nombre)}
              disabled={imprimiendoReporte}
              className="flex flex-col items-center gap-1 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-yellow-50 hover:border-yellow-300 active:bg-yellow-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={`Imprimir ganadores de ${grupo.sorteo_nombre}`}
            >
              <div className="flex items-center gap-1 w-full">
                <Trophy className="text-yellow-500 shrink-0" size={14} />
                <span className="font-semibold text-xs text-gray-900 truncate">{grupo.sorteo_nombre}</span>
                <span className="text-[10px] text-gray-400 shrink-0">{grupo.hora_cierre}</span>
                <Printer size={12} className="text-gray-400 shrink-0 ml-auto" />
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-center">
                {grupo.items.map(g => (
                  <span
                    key={g.id}
                    className={`px-2 py-0.5 rounded text-sm font-bold font-mono ${
                      g.verificado
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {formatearNumeroGanador(g)}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Scanner input */}
      <Card>
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              ref={inputRef}
              type="text"
              value={barcode}
              onChange={e => setBarcode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') consultarTicket(); }}
              placeholder="Escanear o digitar codigo de ticket..."
              className="w-full pl-10 pr-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>
          <button
            onClick={consultarTicket}
            disabled={loading || !barcode.trim()}
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
          {(consulta || error) && (
            <button
              onClick={limpiar}
              className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Limpiar
            </button>
          )}
        </div>
      </Card>

      {/* Mensajes */}
      {exito && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800">
          <CheckCircle size={20} />
          <span className="font-medium">{exito}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800">
          <XCircle size={20} />
          <span className="font-medium">{error}</span>
        </div>
      )}

      {/* Resultado consulta */}
      {consulta && (
        <Card>
          <h3 className="font-semibold text-gray-900 mb-3">Ticket Encontrado</h3>

          {/* Warnings */}
          {consulta.ya_premiado && (
            <div className="flex items-center gap-2 p-2 mb-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
              <AlertTriangle size={16} />
              <span>Este ticket YA fue pagado como premio</span>
            </div>
          )}
          {consulta.anulado && (
            <div className="flex items-center gap-2 p-2 mb-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
              <XCircle size={16} />
              <span>Este ticket fue ANULADO</span>
            </div>
          )}
          {/* Info operativa: quién y cuánto */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Ticket</span>
              <span className="font-mono font-bold text-gray-800">{consulta.ticket_number}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Cliente</span>
              <span className="font-medium text-gray-800">{consulta.cliente_nombre || 'Cliente'}</span>
            </div>
          </div>

          {/* Detalle de jugadas ganadoras */}
          {consulta.jugadas_ganadoras && consulta.jugadas_ganadoras.length > 0 ? (
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Jugadas Ganadoras</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="text-left px-3 py-2 font-semibold">Número</th>
                      <th className="text-right px-3 py-2 font-semibold">Apostado</th>
                      <th className="text-right px-3 py-2 font-semibold">Veces</th>
                      <th className="text-right px-3 py-2 font-semibold">Premio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consulta.jugadas_ganadoras.map((j, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-3 py-2 font-mono font-bold">
                          {j.num1.padStart(2, '0')}{j.num2 ? ` - ${j.num2}` : ''}
                          <span className="text-xs text-gray-400 ml-1">({j.concepto})</span>
                        </td>
                        <td className="px-3 py-2 text-right">{'\u20A1'}{parseFloat(j.monto_apostado).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-medium">{parseFloat(j.multiplicador).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-bold text-green-700">{'\u20A1'}{parseFloat(j.premio).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-blue-50 font-bold">
                      <td colSpan={3} className="px-3 py-3 text-blue-800">TOTAL PREMIO</td>
                      <td className="px-3 py-3 text-right text-2xl text-blue-700">
                        {'\u20A1'}{parseFloat(consulta.premio_calculado).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : (
            <div className="p-3 mb-3 bg-gray-50 border rounded text-gray-500 text-sm text-center">
              No hay jugadas ganadoras verificadas para este ticket
            </div>
          )}

          {/* Info de crédito */}
          {consulta.es_credito && (
            <div className="p-3 mb-3 bg-purple-50 border border-purple-200 rounded">
              <div className="text-sm font-semibold text-purple-800 mb-1">Ticket a CRÉDITO</div>
              <div className="text-sm text-purple-700">
                El premio se aplicará como <strong>abono a la deuda</strong>, no se paga en efectivo.
              </div>
              {consulta.saldo_credito && (
                <div className="mt-2 text-sm">
                  <span className="text-gray-600">Saldo actual: </span>
                  <span className="font-bold">{'\u20A1'}{parseFloat(consulta.saldo_credito).toLocaleString()}</span>
                  {parseFloat(consulta.premio_calculado) > 0 && (
                    <>
                      <span className="text-gray-600 ml-3">Después del abono: </span>
                      <span className="font-bold text-green-700">
                        {'\u20A1'}{(parseFloat(consulta.saldo_credito) - parseFloat(consulta.premio_calculado)).toLocaleString()}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Botón principal PAGAR PREMIO */}
          {puedeConfirmar && (
            <div className="mt-6">
              <button
                onClick={confirmarPago}
                disabled={confirmando}
                className="w-full py-5 bg-green-600 text-white font-bold text-2xl rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg active:scale-[0.98] transition-transform"
              >
                {confirmando ? 'Procesando...' : `PAGAR PREMIO — ₡${parseFloat(consulta.premio_calculado).toLocaleString()}`}
              </button>
            </div>
          )}
        </Card>
      )}

      {/* Historial */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-3">
          Historial de Premios Pagados
          <span className="text-sm font-normal text-gray-500 ml-2">({historial.length})</span>
        </h3>

        {historial.length === 0 ? (
          <p className="text-center text-gray-400 py-6 text-sm">No hay premios pagados</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-3 py-2 font-semibold"># Ticket</th>
                  <th className="text-left px-3 py-2 font-semibold">Hora</th>
                  <th className="text-left px-3 py-2 font-semibold">Usuario</th>
                  <th className="text-right px-3 py-2 font-semibold">Monto Premio</th>
                </tr>
              </thead>
              <tbody>
                {historial.map(p => (
                  <tr key={p.id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono font-medium">{p.ticket_number || p.ticket_id.slice(0, 12)}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {p.created_at ? formatTimeCR(p.created_at) : '-'}
                    </td>
                    <td className="px-3 py-2">{nombreVendedor(p.usuario_id)}</td>
                    <td className="px-3 py-2 text-right font-bold text-green-700">
                      {'\u20A1'}{parseFloat(p.monto).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-3 py-2" colSpan={3}>Total Pagado</td>
                  <td className="px-3 py-2 text-right text-green-700">
                    {'\u20A1'}{historial.reduce((s, p) => s + parseFloat(p.monto), 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ValidacionPremiosPage;
